import { Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ShipperConsigneeForm from 'src/app/MyQuote/CreateQuote/Steps/ShipperConsigneeDetails/ShipperConsigneeForm';
import AdditionalServiceModal from 'src/app/MyQuote/Modals/AdditionalServiceModal';
import LimitedAccessDeliveryModal from 'src/app/MyQuote/Modals/LimitedAccessDeliveryModal';
import { ILimitedAccessDeliveryData, ILimitedAccessDeliveryDataForm } from 'src/app/MyQuote/Modals/quoteDetailModal.interface';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import DeleteModal from 'src/components/DeleteModal';
import MapBox from 'src/components/MapBox/MapBox';
import ShipperConsigneeAutocomplete from 'src/components/ShipperConsigneeAutocomplete/ShipperConsigneeAutocomplete';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { setConsigneeOptions, setConsignees, setShipperOptions, setShippers } from 'src/redux/address.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { deleteSingleAddress } from 'src/services/AddressBookServices';
import { createDateWithoutTimezoneConversion, getMissingAddressMessage, isShipperConsigneeAddressValid, scrollToFirstFormError } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { IShipperAddress, IStopDetailsData } from '../../addQuote.interface';
import { addQuoteShipperDetails, addQuoteStops } from '../../AddQuote.slice';
import { ACTIVITY_HINT, ActivityHintType, getServiceTypesForHint, isWindowTypeValidForHint, stopStepKey } from '../../multiStop.helpers';
import { DATE_TYPE, WINDOW_TYPE } from '../addQuoteSteps.const';
import StepperAction from '../StepperAction';
import { useSaveNextStep } from '../useSaveNextStep';
import { useRegisterStepperPersist } from '../useStepperPersist';

// Middle-stop step (multileg spec §1.2, upgraded Task 11/P1.5, Task 12, Task 13): optional
// activity hint + adaptive date/window + optional per-stop services, plus a full
// ShipperConsigneeAutocomplete-based address picker — same component Stop 1/Final use, not a
// bespoke one (see git history for the prior StopLocationPicker.tsx, replaced by Task 13 for full
// parity: picking/typing a new location here now persists a real address-book record exactly
// like Stop 1/Final, with Edit/Delete on the card). Which list (Shippers vs Consignees) a middle
// stop searches/creates against follows its activityHint — see `isShipper` below — but once a
// location is picked/created, `locationType` freezes which list it belongs to so a later hint
// change never re-looks-up the id in the wrong list. No shared component under src/components/
// is modified — only reused as-is, same as Stop 1/Final.
interface IProps {
    position: number;
    setHeaderChildren: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}

// Activity hint chips (Task 11 §1) — reuses the exact segmented-button pattern StopDetails
// already used for date types (itself lifted from ShipperDetails' pickupSensitivity chips).
// Optional hint only: never blocks progression, never sent to the BE (see multiStop.helpers.ts'
// ACTIVITY_HINT doc comment + buildMultiStopPayload's activityHint-omission test).
const ACTIVITY_HINT_OPTIONS: ActivityHintType[] = [ACTIVITY_HINT.PICKUP, ACTIVITY_HINT.DELIVERY, ACTIVITY_HINT.BOTH];

// `t` typed loosely here (matches getReplacedText's pattern below) — react-i18next's generated
// TFunction type can't verify a key selected via runtime branching against the resource union,
// so an explicit if/else keeps the literal keys type-checkable at their call site (en.json) while
// still routing through i18n rather than hardcoding English text.
const getActivityHintLabel = (t: any, value: ActivityHintType): string => {
    if (value === ACTIVITY_HINT.PICKUP) return t('MyQuote.Activity_Pickup');
    if (value === ACTIVITY_HINT.DELIVERY) return t('MyQuote.Activity_Delivery');

    return t('MyQuote.Activity_Both');
};

// EDITABLE_SERVICES_SLUGS mirrors ShipperDetails'/ConsigneeDetails' constant of the same name.
// `in-bond`, `load_insurance`, and `dangerous_goods` live under serviceTypes.OTHER (collected on
// the Dimensions step instead — see Dimensions/index.tsx), so they never actually reach a
// PICKUP/DELIVERY list — this array is used ONLY to gate the pencil-icon-once-checked pattern
// below (mirroring ConsigneeDetails exactly), never to filter the rendered list itself.
//
// Task 22 (client decision) — full parity: `limited_access_delivery` now gets the same real
// pencil-icon + LimitedAccessDeliveryModal wiring ConsigneeDetails has (see handleEditService/
// handleLimitedAccess below), backed by real persistence — createMultiStopQuote.controller.ts
// writes it to quote_selected_services keyed by stopId (quoteId left null). This supersedes an
// earlier P1.5 decision that filtered it out of the list entirely for lack of that plumbing.
const EDITABLE_SERVICES_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods', 'limited_access_delivery'];
const textReplacements = (t: any) => [
    { original: 'After Hours / Weekend / Holiday Pickup', replacement: t('QuotingHub.Common.After_Hours_Pickup') },
    { original: 'Pickup Appointment Required', replacement: t('QuotingHub.Common.Pickup_Appointment') },
    { original: 'Delivery Appointment Required', replacement: t('QuotingHub.Common.Delivery_Appointment') },
];

const initStopData = (): IStopDetailsData => ({
    companyName: '',
    stopAddress: null,
    // Task 15 — Open restored as the default (date is optional again).
    dateType: DATE_TYPE.OPEN,
    date: null,
    endDate: null,
    windowType: null,
    startTime: null,
    endTime: null,
    serviceId: [],
    activityHint: ACTIVITY_HINT.BOTH,
    locationId: null,
    locationType: null,
    limitedAccessDelivery: false,
    limitedAccessDeliveryType: '',
    limitedAccessDeliveryOther: '',
});

const INITIAL_DELETE_ACTION = { mode: '' };

const StopDetails: FC<IProps> = ({ position, setHeaderChildren }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const stops = useAppSelector(addQuoteStops);
    const shipperDetails = useAppSelector(addQuoteShipperDetails);
    const { serviceTypes } = useAppSelector((state: RootState) => state.basic);
    const { onSaveNext, onBack, onPersist } = useSaveNextStep();
    const serviceRefName = useRef<HTMLParagraphElement>(null);
    const { shipperOptions, consigneeOptions, shippers, consignees, handleSearchShipper, handleSearchConsignee } = useAddressDetails();

    const savedData = stops[position];

    const [formData, setFormData] = useState<IStopDetailsData>(savedData ?? initStopData());
    const [submitted, setSubmitted] = useState(false);
    // Task 21 (client decision) — Pickup/Delivery services now render as two separate sections
    // (matching ShipperDetails'/ConsigneeDetails' own info-icon-per-section pattern exactly)
    // rather than one merged box, so the info modal needs to know which section opened it.
    const [serviceInfoModal, setServiceInfoModal] = useState<'pickup' | 'delivery' | null>(null);
    // Task 22 — mirrors ConsigneeDetails' `action.limitedAccess` flag for the Limited Access
    // Delivery detail modal (separate from serviceInfoModal, which is the info-icon tooltip modal).
    const [showLimitedAccessModal, setShowLimitedAccessModal] = useState(false);

    // Task 13 — ShipperConsigneeAutocomplete requires a react-hook-form `control` bound via its
    // own internal <Controller>. Nothing else on this step needs RHF (it's plain useState like
    // before), so this is a small, scoped form hosting ONLY `locationId` — not a full migration.
    const { control: locationControl, setValue: setLocationValue } = useForm<{ locationId: number | null }>({
        defaultValues: { locationId: savedData?.locationId ?? null },
    });
    const [isEditId, setIsEditId] = useState(false);
    const [preLocationId, setPreLocationId] = useState<number | null>(null);
    const [deleteAction, setDeleteAction] = useState(INITIAL_DELETE_ACTION);
    const [isDeleting, setIsDeleting] = useState(false);

    // Default to 'both' for stops saved before Task 11 shipped (activityHint is optional/additive).
    const activityHint: ActivityHintType = formData.activityHint ?? ACTIVITY_HINT.BOTH;
    // Review fix (Finding 2) — drop EDITABLE_SERVICES_SLUGS (see constant doc comment above) from
    // both the checkbox picker and the info modal so the two stay in sync. Still the UNION list
    // (Task 11's getServiceTypesForHint) — kept for handleSaveLocation's allowedIds check below,
    // which needs to recognize a saved address-book record's services from EITHER direction when
    // the stop is hinted "both". The two split render lists (pickupServices/deliveryServices,
    // Task 21) are what's actually shown on screen now — see the render section.
    const stopServices = getServiceTypesForHint(serviceTypes, activityHint).filter((val: any) => !EDITABLE_SERVICES_SLUGS.includes(val.slug));
    // Task 21 (client decision) — Pickup and Delivery services are always their OWN raw lists
    // (never merged/deduped for display), same as ShipperDetails'/ConsigneeDetails' own sections.
    // Which section(s) render is driven by the hint: Pick up → pickup only, Deliver → delivery
    // only, Both (or unset/legacy) → both sections stacked.
    // Task 22 — `limited_access_delivery` is deliberately NOT excluded here (unlike the other
    // three EDITABLE_SERVICES_SLUGS, which are no-ops for PICKUP/DELIVERY anyway) — it now renders
    // like every other checkbox, with its pencil-icon/modal handled separately below, mirroring
    // ConsigneeDetails exactly (its own list render also excludes nothing).
    const NON_STOP_SUPPORTED_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods'];
    const pickupServices = (serviceTypes?.PICKUP || []).filter((val: any) => !NON_STOP_SUPPORTED_SLUGS.includes(val.slug));
    const deliveryServices = (serviceTypes?.DELIVERY || []).filter((val: any) => !NON_STOP_SUPPORTED_SLUGS.includes(val.slug));
    const showPickupServices = activityHint === ACTIVITY_HINT.PICKUP || activityHint === ACTIVITY_HINT.BOTH;
    const showDeliveryServices = activityHint === ACTIVITY_HINT.DELIVERY || activityHint === ACTIVITY_HINT.BOTH;

    // Task 13 — which address-book list this stop searches/creates against. Once a location is
    // picked/created, its own recorded `locationType` wins (permanent for that record — see
    // addQuote.interface.ts); only an EMPTY stop lets the current hint decide which list a new
    // search hits. Deliver hint alone routes to Consignees; Pick up or Both route to Shippers.
    const isShipper = formData.locationType ? formData.locationType === 'shipper' : activityHint !== ACTIVITY_HINT.DELIVERY;

    // Task 16 — a middle stop's arrival date must be on or after Stop 1's pickup date (same-day
    // is fine — a local multi-stop route can pick up and reach a later stop the same day; it just
    // can't be BEFORE pickup). `minStopDate` is pickup date itself (not +1 day) so the calendar
    // allows the same day; pickupDate is undefined until Stop 1 is filled in (first step in the
    // flow), so this only activates once there's something to compare against.
    const pickupDate = shipperDetails?.pickupDate || null;
    const minStopDate = pickupDate ? createDateWithoutTimezoneConversion(moment(pickupDate)) : undefined;

    // If Stop 1's pickup date changes (the rep navigated back and picked a later date) AFTER this
    // stop already had a date that's no longer valid, clear it rather than silently submitting an
    // impossible date — same "reset what's no longer compatible" rule used for windowType above.
    useEffect(() => {
        if (!formData.date || !minStopDate) return;

        const currentDate = createDateWithoutTimezoneConversion(moment(formData.date));

        if (currentDate < minStopDate) {
            setFormData((prev) => ({ ...prev, date: null }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickupDate]);

    // Appointment auto-select parity (review Finding 1) — mirrors ShipperDetails'/ConsigneeDetails'
    // prevPickupWindowType/prevDeliveryWindowType effects: switching the window to "By appointment"
    // auto-checks the matching appointment-required service; switching away un-checks it. Unlike
    // the bookend steps (one fixed direction each), a middle stop's hint can be pickup, delivery,
    // or both, so "matching" follows getServiceTypesForHint — pickup hint checks only
    // pickup_appointment_required, delivery hint checks only delivery_appointment_required, and
    // both hint checks both (since "both" is the union of the PICKUP + DELIVERY lists, both
    // checkboxes are visible together). Tracked via a combined windowType+hint key (instead of the
    // references' single windowType ref) so switching hint alone — window already on appointment —
    // also re-syncs which slug(s) are checked.
    const prevAppointmentKeyRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        const key = `${formData.windowType}|${activityHint}`;
        const prev = prevAppointmentKeyRef.current;
        prevAppointmentKeyRef.current = key;

        // Skip on mount (prev === undefined) so loading a saved stop never overwrites its stored
        // serviceId selection — only subsequent user-driven changes trigger the sync.
        if (prev === undefined || prev === key) return;

        const isAppointment = formData.windowType === WINDOW_TYPE.APPOINTMENT;
        const wantsPickupAppointment = isAppointment && (activityHint === ACTIVITY_HINT.PICKUP || activityHint === ACTIVITY_HINT.BOTH);
        const wantsDeliveryAppointment = isAppointment && (activityHint === ACTIVITY_HINT.DELIVERY || activityHint === ACTIVITY_HINT.BOTH);

        const pickupAppointmentId: number | undefined = (serviceTypes?.PICKUP || []).find((s: any) => s.slug === 'pickup_appointment_required')?.id;
        const deliveryAppointmentId: number | undefined = (serviceTypes?.DELIVERY || []).find((s: any) => s.slug === 'delivery_appointment_required')?.id;

        setFormData((prev2) => {
            let nextIds = prev2.serviceId;

            const applyToggle = (id: number | undefined, shouldSelect: boolean) => {
                if (id === undefined) return;
                const isSelected = nextIds.includes(id);

                if (shouldSelect && !isSelected) nextIds = [...nextIds, id];
                else if (!shouldSelect && isSelected) nextIds = nextIds.filter((existingId) => existingId !== id);
            };

            applyToggle(pickupAppointmentId, wantsPickupAppointment);
            applyToggle(deliveryAppointmentId, wantsDeliveryAppointment);

            return nextIds === prev2.serviceId ? prev2 : { ...prev2, serviceId: nextIds };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.windowType, activityHint]);

    // Handles both a full Google Places selection object AND the "cleared" cases
    // (blur with no text, explicit clear) — mirrors ShipperDetails' handleAutocompleteChange.
    const handleAddressChange = (value: any) => {
        if (!value) {
            setFormData((prev) => ({ ...prev, stopAddress: null }));

            return;
        }

        if (value.type === 'blur' || (value.target && value.type)) {
            return;
        }

        if (typeof value === 'object' && (value.fullAddress === '' || (!value.fullAddress && !value.address1 && !value.city))) {
            setFormData((prev) => ({ ...prev, stopAddress: null }));

            return;
        }

        if (typeof value === 'object' && value.fullAddress !== undefined) {
            const nextAddress: IShipperAddress = {
                fullAddress: value.fullAddress ?? '',
                address1: value.address1 ?? '',
                city: value.city ?? '',
                country: value.country ?? '',
                countryCode: value.countryCode ?? '',
                latitude: value.latitude ?? '',
                longitude: value.longitude ?? '',
                postal: value.postal ?? '',
                state: value.state ?? '',
                stateCode: value.stateCode ?? '',
                streetNumber: value.streetNumber ?? '',
                timezone: value.timezone ?? '',
            };

            setFormData((prev) => ({ ...prev, stopAddress: nextAddress }));
        }
    };

    // Task 13 — writes locationId/locationType (+ optionally companyName/stopAddress) into both
    // formData (the actual source of truth for this step) and the mini RHF form's `locationId`
    // field (so ShipperConsigneeAutocomplete's own <Controller> displays the current value).
    const setLocationData = (next: { locationId: number | null; locationType: 'shipper' | 'consignee' | null; companyName?: string; stopAddress?: IShipperAddress | null }) => {
        setLocationValue('locationId', next.locationId);
        setFormData((prev) => ({
            ...prev,
            locationId: next.locationId,
            locationType: next.locationType,
            ...(next.companyName !== undefined ? { companyName: next.companyName } : {}),
            ...(next.stopAddress !== undefined ? { stopAddress: next.stopAddress } : {}),
        }));
    };

    const resetLocationForm = () => {
        setLocationData({ locationId: null, locationType: null, companyName: '', stopAddress: null });
    };

    // Mirrors ShipperDetails'/ConsigneeDetails' handleShipperChange/handleConsigneeChange —
    // resolves the picked option against whichever list `isShipper` currently points at.
    const handleLocationChange = (onChange: any) => (value: any) => {
        if (value) {
            onChange(value.value);
            const source = isShipper ? shippers : consignees;
            const data = (source || []).find((s: any) => s.id === value.value);

            if (data) {
                setLocationData({
                    locationId: data.id,
                    locationType: isShipper ? 'shipper' : 'consignee',
                    companyName: data.companyName,
                    stopAddress: data,
                });

                if (!isShipperConsigneeAddressValid(data)) {
                    setIsEditId(true);
                }
            }
        } else {
            onChange(null);
            resetLocationForm();
        }
    };

    // Mirrors ShipperDetails' handleGoogleAddressSelect — typing a raw address clears any
    // previously-picked id and opens ShipperConsigneeForm so the rep completes + persists it
    // (Task 13: full parity means a middle stop's "new address" is a real address-book record
    // too, not transient quote-only data like the prior StopLocationPicker kept it).
    const handleGoogleAddressSelect = (addressData: any) => {
        setLocationData({
            locationId: null,
            locationType: null,
            // Same as ShipperDetails: a Google establishment hands back its business name, so the
            // stop's form opens with the name already filled; a plain street address returns ''.
            companyName: addressData?.companyName ?? '',
            stopAddress: {
                fullAddress: addressData?.fullAddress ?? '',
                address1: addressData?.address1 ?? '',
                city: addressData?.city ?? '',
                country: addressData?.country ?? '',
                countryCode: addressData?.countryCode ?? '',
                latitude: addressData?.latitude ?? '',
                longitude: addressData?.longitude ?? '',
                postal: addressData?.postal ?? '',
                state: addressData?.state ?? '',
                stateCode: addressData?.stateCode ?? '',
                streetNumber: addressData?.streetNumber ?? '',
                timezone: addressData?.timezone ?? '',
                additionalServiceIds: addressData?.additionalServiceIds ?? [],
            },
        });
        setIsEditId(true);
    };

    // Mirrors ShipperDetails' handleCardActionType — switch/edit/delete on the selected-location card.
    const handleCardActionType = (value: string) => () => {
        if (value === 'edit_id') {
            setPreLocationId(formData.locationId ?? null);
            setIsEditId(true);
        } else if (value === 'delete_id') {
            setDeleteAction({ mode: 'delete' });
        } else if (value === 'switch_id') {
            resetLocationForm();
        }
    };

    const handleCreateNew = () => {
        setIsEditId(true);
        resetLocationForm();
    };

    // Mirrors ShipperDetails' handleEditShipper (the restore-on-cancel variant — ConsigneeDetails'
    // equivalent always clears on cancel instead of restoring, which the research pass flagged as
    // an inconsistency between the two; standardizing on the more correct behavior here since one
    // function now serves both directions).
    const handleEditLocation = (isEdit?: boolean) => () => {
        setIsEditId(isEdit ?? true);

        if (!isEdit) {
            const isValidId = preLocationId && preLocationId === formData.locationId;

            if (isValidId) {
                setPreLocationId(null);
            } else {
                resetLocationForm();
            }
        }
    };

    // Mirrors ShipperDetails' handleSaveShipper — receives the persisted record back from
    // ShipperConsigneeForm's Save, then auto-fills serviceId from the record's stored additional
    // services (same as Shipper/Consignee), filtered to whatever this stop's hint actually offers
    // so we never silently select a service the picker doesn't show for this stop.
    const handleSaveLocation = (data: any) => {
        setIsEditId(false);
        setLocationData({
            locationId: data.id,
            locationType: isShipper ? 'shipper' : 'consignee',
            companyName: data.companyName,
            stopAddress: data,
        });

        const savedServiceIds: number[] = (data.pickupAdditionalService ?? data.additionalServiceIds ?? []).map(Number).filter(Boolean);
        const allowedIds = new Set(stopServices.map((s: any) => s.id));
        const filteredIds = savedServiceIds.filter((id: number) => allowedIds.has(id));

        if (filteredIds.length) {
            setFormData((prev) => ({ ...prev, serviceId: Array.from(new Set([...prev.serviceId, ...filteredIds])) }));
        }

        setPreLocationId(null);
    };

    // Mirrors ShipperDetails' handleShipperConsigneeInputChange — searches whichever list
    // `isShipper` currently points at.
    const handleLocationInputChange = (e: any, { action: inputAction }: any) => {
        if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
            if (isShipper) handleSearchShipper(e);
            else handleSearchConsignee(e);
        }
    };

    // Mirrors ShipperDetails' handleModalClose — deletes the persisted record and updates the
    // shared address-book redux lists so other steps (Stop 1/Final, other middle stops) see it too.
    const handleDeleteModalClose = (closeStatus: boolean) => () => {
        setDeleteAction(INITIAL_DELETE_ACTION);

        if (closeStatus) {
            setIsDeleting(true);
            const idToDelete = formData.locationId ?? null;

            if (idToDelete) {
                deleteSingleAddress(idToDelete.toString())
                    .then((result: any) => {
                        if (result) {
                            resetLocationForm();

                            if (isShipper) {
                                dispatch(setShippers(shippers.filter((s: any) => s.id !== idToDelete)));
                                dispatch(setShipperOptions(shipperOptions.filter((o: any) => o.value !== idToDelete)));
                            } else {
                                dispatch(setConsignees(consignees.filter((c: any) => c.id !== idToDelete)));
                                dispatch(setConsigneeOptions(consigneeOptions.filter((o: any) => o.value !== idToDelete)));
                            }
                        }
                    })
                    .catch((error: any) => {
                        WalToast.error(error?.response?.data?.message || t('ValidationMessages.Shipper_delete_failed'));
                    })
                    .finally(() => setIsDeleting(false));
            } else {
                setIsDeleting(false);
            }
        }
    };

    // Switching the activity hint keeps entered values where compatible — only resets
    // windowType (and its times) when the currently-selected window isn't offered under the new
    // hint; the date is always kept (Task 11 §2).
    const handleHintChange = (hint: ActivityHintType) => {
        setFormData((prev) => {
            const windowValid = isWindowTypeValidForHint(prev.windowType, hint);

            return {
                ...prev,
                activityHint: hint,
                windowType: windowValid ? prev.windowType : null,
                startTime: windowValid ? prev.startTime : null,
                endTime: windowValid ? prev.endTime : null,
            };
        });
    };

    // Task 22 — mirrors ConsigneeDetails' handleServiceChange exactly: checking
    // "limited_access_delivery" does NOT add it to serviceId directly — it opens the modal
    // instead, and the id only gets added once the modal saves (handleLimitedAccess below).
    // Unchecking clears all three limitedAccessDelivery* fields back to false/''/'' in addition
    // to removing the id from serviceId.
    const handleServiceChange = (e: any) => {
        const { checked, name } = e.target;
        const value = parseInt(e.target.value, 10);

        if (checked && name === 'limited_access_delivery') {
            setShowLimitedAccessModal(true);

            return;
        }

        setFormData((prev) => ({
            ...prev,
            serviceId: checked ? [...prev.serviceId, value] : prev.serviceId.filter((id) => id !== value),
            ...(name === 'limited_access_delivery' && !checked ? { limitedAccessDelivery: false, limitedAccessDeliveryType: '', limitedAccessDeliveryOther: '' } : {}),
        }));
    };

    // Task 22 — mirrors ConsigneeDetails' handleEditService (curried, opens the modal for
    // re-editing via the pencil icon once the checkbox is already checked).
    const handleEditService = (service: any) => () => {
        if (service?.slug === 'limited_access_delivery') {
            setShowLimitedAccessModal(true);
        }
    };

    // Task 22 — mirrors ConsigneeDetails' handleLimitedAccess exactly, including the
    // `selectedLimitedAccessType` field-name asymmetry (the modal's save payload uses that name,
    // not `limitedAccessType`). Adds the service id to serviceId only now, on save.
    const handleLimitedAccess = (data: ILimitedAccessDeliveryData) => {
        const limitedAccessId = serviceTypes?.DELIVERY?.find((val: any) => val.slug === 'limited_access_delivery')?.id;

        setFormData((prev) => ({
            ...prev,
            limitedAccessDelivery: true,
            limitedAccessDeliveryType: data.selectedLimitedAccessType,
            limitedAccessDeliveryOther: data.limitedAccessOther,
            serviceId: limitedAccessId && !prev.serviceId.includes(Number(limitedAccessId)) ? [...prev.serviceId, Number(limitedAccessId)] : prev.serviceId,
        }));
        setShowLimitedAccessModal(false);
    };

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements(t).find((item) => item.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    // Task 21 — shared checkbox grid for both the Pickup and Delivery services sections below
    // (identical markup, just a different source list) — same shape ShipperDetails/ConsigneeDetails
    // render for their own single-direction section. Task 22 adds the pencil-icon-once-checked
    // pattern for EDITABLE_SERVICES_SLUGS, mirroring ConsigneeDetails' getList exactly.
    const renderServiceCheckboxes = (list: any[]) => (
        <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">
            {list.map((val: any) =>
                val?.type !== 'OTHER' || val?.slug === 'load_insurance' || val.loadLinkTerm !== null ? (
                    <div className="flex w-[calc(33.33%_-_8px)]" key={`stop_service_${val.id}`}>
                        <CheckBox
                            id={`stop_service_${position}_${val.id}`}
                            onChangeFunc={handleServiceChange}
                            name={val.slug}
                            value={val.id}
                            checked={formData.serviceId.includes(val.id)}
                            parentClassName="mt-px"
                        />
                        <div className="pl-2 leading-4 text-textSecondary text-xs font-medium truncate flex-1">
                            {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                                <TooltipCmp message={val.name} childrenClassName="!flex">
                                    <label htmlFor={`stop_service_${position}_${val.id}`} className="cursor-pointer truncate">
                                        {getReplacedText(val.name)}
                                    </label>
                                </TooltipCmp>
                            ) : (
                                <TruncateTextCmp message={val.name} refName={serviceRefName}>
                                    <p className="truncate" ref={serviceRefName}>
                                        <label htmlFor={`stop_service_${position}_${val.id}`} className="cursor-pointer">
                                            {getReplacedText(val.name)}
                                        </label>
                                    </p>
                                </TruncateTextCmp>
                            )}
                        </div>
                        {formData.serviceId.includes(val.id) && EDITABLE_SERVICES_SLUGS.includes(val.slug) ? (
                            <TooltipCmp message={t('Common.Edit')}>
                                <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                            </TooltipCmp>
                        ) : null}
                    </div>
                ) : null
            )}
        </div>
    );

    // Task 15 — reverted Task 14's "required" rule: a date is optional again (Open is back), but
    // if the rep DOES pick one it still has to make sense — after Stop 1's pickup date, and (for
    // Flexible) end on/after the start date. Missing is fine; wrong-order isn't.
    const isFlexible = formData.dateType === DATE_TYPE.FLEXIBLE;
    const isDateTooEarly = !!(formData.date && minStopDate && createDateWithoutTimezoneConversion(moment(formData.date)) < minStopDate);
    const isEndDateTooEarly = !!(
        isFlexible &&
        formData.date &&
        formData.endDate &&
        createDateWithoutTimezoneConversion(moment(formData.endDate)) < createDateWithoutTimezoneConversion(moment(formData.date))
    );

    // DEVTEAM-4976 #15 — a city-level Google pick ("Toronto, ON, Canada") used to satisfy this step
    // because only `fullAddress` was checked, so Next went through on a half-entered address.
    // Stop addresses now have to be as complete as Shipper/Consignee's, using the same
    // `isShipperConsigneeAddressValid` rule their yup schemas already enforce.
    const isStopAddressIncomplete = !!formData.stopAddress?.fullAddress && !isShipperConsigneeAddressValid(formData.stopAddress);

    const handleNext = () => {
        setSubmitted(true);

        if (!formData.stopAddress?.fullAddress || isStopAddressIncomplete) {
            // This step keeps its values in local state rather than react-hook-form, so there is no
            // errors object to walk — the address is the only user-blocking field here (the date
            // guards below cover values restored from a saved stop; this step renders no date input).
            scrollToFirstFormError([`stop_address_${position}`]);

            return;
        }

        if (isDateTooEarly || isEndDateTooEarly) return;

        onSaveNext(formData, stopStepKey(position));
    };

    const handleBack = () => {
        onBack(formData, stopStepKey(position));
    };

    // Sidebar persist — this step holds its values in local `formData` state (not react-hook-form),
    // so the in-flight object is written to the slice as-is, the same payload Next/Back store.
    useRegisterStepperPersist(() => {
        onPersist(formData, stopStepKey(position));
    });

    useEffect(() => {
        // DEVTEAM-4976 #15 — Next stays disabled while the inline address form is open, matching
        // ShipperDetails'/ConsigneeDetails' own `isNextButtonDisabled={isEditId}`: the rep has to
        // finish and save the address record before the wizard will move on.
        setHeaderChildren(<StepperAction handleNext={handleNext} handleBack={handleBack} nextButtonText={t('Common.Next')} isNextButtonDisabled={isEditId} />);

        return () => setHeaderChildren(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, isEditId]);

    const showAddressError = submitted && (!formData.stopAddress?.fullAddress || isStopAddressIncomplete);
    // Name the parts Google left out (same wording the address form's warning tooltip uses) rather
    // than repeating the generic "Address" label, so the rep knows what still has to be filled in.
    const addressErrorMessage = (isStopAddressIncomplete && getMissingAddressMessage(formData.stopAddress, t)) || t('MyQuote.Stop_Address');

    return (
        <>
            {/* Activity hint (Task 11 §1) — optional, defaults to Both, never blocks Next */}
            <div className="stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('MyQuote.What_Happens_Here')}</label>
                </div>
                <div className="flex-1">
                    <div className="flex flex-wrap gap-2">
                        {ACTIVITY_HINT_OPTIONS.map((hintOption) => {
                            const isSelected = activityHint === hintOption;

                            return (
                                <ButtonCmp key={hintOption} type="button" className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'} onClick={() => handleHintChange(hintOption)}>
                                    {getActivityHintLabel(t, hintOption)}
                                </ButtonCmp>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Address (required) — Task 13: same ShipperConsigneeAutocomplete + MapBox + Add-New
                flow Stop 1/Final use. There's no separate free-text "Company" field anymore — like
                Shipper/Consignee, the company name lives on the picked/created address-book
                record itself (shown on the card header once one is selected). */}
            {!isEditId && (
                <div id={`stop_address_${position}`} className="border-t border-utilityGray200 stepper-content-container">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common">
                            {t('MyQuote.Stop_Address')}
                            <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                        </label>
                    </div>
                    <div className="flex-1 space-y-3">
                        <ShipperConsigneeAutocomplete
                            control={locationControl}
                            isShipper={isShipper}
                            isEditId={isEditId}
                            watchCompanyName={formData.companyName}
                            watchShipperAddress={formData.stopAddress}
                            watchShipperFullAddress={formData.stopAddress?.fullAddress}
                            shipperOptions={shipperOptions}
                            consigneeOptions={consigneeOptions}
                            errors={
                                {
                                    shipperAddress: showAddressError ? { message: addressErrorMessage } : undefined,
                                    consigneeAddress: showAddressError ? { message: addressErrorMessage } : undefined,
                                } as any
                            }
                            handleShipperChange={handleLocationChange}
                            handleCreateNew={handleCreateNew}
                            handleCardActionType={handleCardActionType}
                            onInputChange={handleLocationInputChange}
                            fieldName="locationId"
                            isQuoteCreateForm={true}
                            showDeleteOption={!!formData.locationId}
                            handleGoogleAddressSelect={handleGoogleAddressSelect}
                            watchShipperConsigneeId={formData.locationId}
                            isOpenFromStop
                        />
                        <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden">
                            <MapBox
                                draggable={false}
                                address={formData.stopAddress?.fullAddress ? formData.stopAddress : null}
                                id={`stopMap_${position}`}
                                onReverseAddressFunc={handleAddressChange}
                                className="rounded-lg bg-white h-full border border-utilityGray200"
                                isDefaultSatelliteView={true}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Wrapper keeps ShipperConsigneeForm's rows out of StepperLayout's
                `space-y-5 [&>*:not(.field-without-top-border)]:pt-5` direct-child rule, so they
                space themselves with their own pt-5 (20px) exactly like ShipperDetails'/
                ConsigneeDetails' edit form instead of stacking margin + padding (40px). */}
            {isEditId && (
                <div className="border-t border-utilityGray200">
                    <ShipperConsigneeForm
                        handleEditShipper={handleEditLocation}
                        id={formData.locationId ? +formData.locationId : undefined}
                        handleSaveShipper={handleSaveLocation}
                        isShipper={isShipper}
                        defaultCompanyName={formData.companyName || ''}
                        defaultAddress={(formData.stopAddress as any) || null}
                        showAddressIncompleteWarning
                        isOpenFromStop
                    />
                </div>
            )}

            {deleteAction.mode === 'delete' && (
                <DeleteModal
                    handleClose={handleDeleteModalClose}
                    moduleTitle={t('Orders.remove_from_wal_hq', { companyName: formData.companyName || '' })}
                    moduleName={t(isShipper ? 'Common.Shipper' : 'Common.Consignee')}
                    isDeleteLoading={isDeleting}
                />
            )}

            {/* Additional services (Task 11 §3, split into two sections per Task 21 client
                decision) — same source (serviceTypes.PICKUP/.DELIVERY from state.basic), same
                CheckBox shape, same serviceId: number[] storage as before, but rendered as two
                separate sections — one Pickup, one Delivery — exactly matching ShipperDetails'/
                ConsigneeDetails' own single-direction sections (same labels, same per-section
                info icon/modal), instead of one merged/deduped box. A hint of Pick up or Deliver
                shows only its own section; Both (or unset/legacy) shows both, stacked. */}
            {showPickupServices && (
                // Task 24 — border-b/pb-5 only when Delivery ISN'T also rendering right after
                // (Both hint): otherwise this section's bottom border sits flush against
                // Delivery's top border, showing as two near-overlapping horizontal lines.
                <div className={`border-t border-utilityGray200 stepper-content-container ${!showDeliveryServices ? 'border-b pb-5' : ''}`}>
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common flex items-center gap-x-0.5">
                            {t('QuotingHub.Common.Pickup_Additional_Services')}
                            <div className="cursor-pointer" onClick={() => setServiceInfoModal('pickup')}>
                                <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                            </div>
                        </label>
                    </div>
                    <div className="flex-1">{renderServiceCheckboxes(pickupServices)}</div>
                </div>
            )}

            {showDeliveryServices && (
                <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common flex items-center gap-x-0.5">
                            {t('QuotingHub.Common.Delivery_Additional_Services')}
                            <div className="cursor-pointer" onClick={() => setServiceInfoModal('delivery')}>
                                <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                            </div>
                        </label>
                    </div>
                    <div className="flex-1">{renderServiceCheckboxes(deliveryServices)}</div>
                </div>
            )}

            {serviceInfoModal === 'pickup' && <AdditionalServiceModal handleClose={() => setServiceInfoModal(null)} services={pickupServices} title={`Pickup ${t('Common.Additional_Services')}`} />}
            {serviceInfoModal === 'delivery' && (
                <AdditionalServiceModal handleClose={() => setServiceInfoModal(null)} services={deliveryServices} title={`Delivery ${t('Common.Additional_Services')}`} />
            )}

            {showLimitedAccessModal && (
                <LimitedAccessDeliveryModal
                    handleClose={() => setShowLimitedAccessModal(false)}
                    handleLimitedAccess={handleLimitedAccess}
                    defaultFormValues={
                        {
                            limitedAccessType: formData.limitedAccessDeliveryType,
                            limitedAccessOther: formData.limitedAccessDeliveryOther,
                        } as ILimitedAccessDeliveryDataForm
                    }
                />
            )}
        </>
    );
};

export default StopDetails;
